import React, { useCallback, useRef, useState } from 'react'
import { makeStyles, Typography, Box, IconButton, Divider, Popover, TextField, Button } from '@material-ui/core'
import cn from 'classnames'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import { KnowledgeBaseImage } from './knowledgeBaseImage'

const useStyles = makeStyles((theme) => ({
  wrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
  },
  wrapperError: {
    borderColor: theme.palette.error.main,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 88,
    zIndex: 5,
    backgroundColor: theme.palette.background.paper,
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
  },
  tableBubble: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.2rem',
    padding: '0.25rem 0.35rem',
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
  },
  toolButton: {
    padding: '0.35rem',
    minWidth: '2rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    borderRadius: '4px',
    color: theme.palette.text.primary,
  },
  toolButtonActive: {
    backgroundColor: theme.palette.action.selected,
  },
  colorInput: {
    width: '1.75rem',
    height: '1.75rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  linkPopover: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem 0.75rem 0.75rem',
    minWidth: '20rem',
    overflow: 'visible',
  },
  content: {
    '& .ProseMirror': {
      minHeight: '18rem',
      padding: '0.75rem 1rem',
      outline: 'none',
      color: theme.palette.text.secondary,
    },
    '& .ProseMirror table': {
      borderCollapse: 'collapse',
      width: '100%',
    },
    '& .ProseMirror th, & .ProseMirror td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '0.4rem 0.6rem',
    },
    '& .ProseMirror th': {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 600,
    },
    '& .ProseMirror img': {
      maxWidth: '100%',
      height: 'auto',
    },
    '& .ProseMirror a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  formHelper: {
    fontSize: '0.875rem',
    color: theme.palette.error.main,
    marginTop: '0.4rem',
  },
}))

interface ToolButtonProps {
  label: string
  onClick: () => void
  active?: boolean
}

interface Props {
  value: string
  onChange: (html: string) => void
  onUploadImage?: (file: File) => Promise<string>
  error?: boolean
  helperText?: string | false
}

const TipTapEditor = ({ value, onChange, onUploadImage, error, helperText }: Props) => {
  const s = useStyles()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null)
  const [linkUrl, setLinkUrl] = useState('')

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      KnowledgeBaseImage,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  })

  const ToolButton = ({ label, onClick, active }: ToolButtonProps) => (
    <IconButton
      size="small"
      className={cn(s.toolButton, active && s.toolButtonActive)}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {label}
    </IconButton>
  )

  const openLinkPopover = (event: React.MouseEvent<HTMLElement>) => {
    if (!editor) return
    setLinkUrl(editor.getAttributes('link').href || 'https://')
    setLinkAnchor(event.currentTarget)
  }

  const applyLink = () => {
    if (!editor) return
    const chain = editor.chain().focus().extendMarkRange('link')
    if (linkUrl.trim() === '' || linkUrl.trim() === 'https://') chain.unsetLink().run()
    else chain.setLink({ href: linkUrl.trim() }).run()
    setLinkAnchor(null)
  }

  const handleImageInput = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || !editor || !onUploadImage) return
      const url = await onUploadImage(file)
      editor.chain().focus().setImage({ src: url }).run()
    },
    [editor, onUploadImage]
  )

  if (!editor) return null

  const toggleHeading = (level: 1 | 2 | 3) => editor.chain().focus().toggleHeading({ level }).run()
  const setImageWidth = (width: string) => editor.chain().focus().updateAttributes('image', { width }).run()
  const setImageAlign = (align: string) => editor.chain().focus().updateAttributes('image', { align }).run()
  const setTextAlign = (align: string) => editor.chain().focus().setTextAlign(align).run()

  return (
    <>
      <Box className={cn(s.wrapper, error && s.wrapperError)}>
        <Box className={s.toolbar}>
          <ToolButton label="H1" active={editor.isActive('heading', { level: 1 })} onClick={() => toggleHeading(1)} />
          <ToolButton label="H2" active={editor.isActive('heading', { level: 2 })} onClick={() => toggleHeading(2)} />
          <ToolButton label="H3" active={editor.isActive('heading', { level: 3 })} onClick={() => toggleHeading(3)} />
          <Divider orientation="vertical" flexItem />
          <ToolButton label="B" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
          <ToolButton label="I" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
          <ToolButton label="U" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
          <input
            type="color"
            className={s.colorInput}
            title="Text color"
            value={editor.getAttributes('textStyle').color || '#000000'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
          <Divider orientation="vertical" flexItem />
          <ToolButton label="• List" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
          <ToolButton label="1. List" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
          <Divider orientation="vertical" flexItem />
          <ToolButton label="Left" active={editor.isActive({ textAlign: 'left' })} onClick={() => setTextAlign('left')} />
          <ToolButton label="Center" active={editor.isActive({ textAlign: 'center' })} onClick={() => setTextAlign('center')} />
          <ToolButton label="Right" active={editor.isActive({ textAlign: 'right' })} onClick={() => setTextAlign('right')} />
          <Divider orientation="vertical" flexItem />
          <ToolButton label="Table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />
          <IconButton
            size="small"
            className={cn(s.toolButton, editor.isActive('link') && s.toolButtonActive)}
            onMouseDown={(event) => event.preventDefault()}
            onClick={openLinkPopover}
          >
            Link
          </IconButton>
          <ToolButton label="Image" onClick={() => fileInputRef.current && fileInputRef.current.click()} />
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageInput} />
        </Box>
        <BubbleMenu
          editor={editor}
          pluginKey="kbTableBubble"
          shouldShow={({ editor: current }) => current.isActive('table')}
          tippyOptions={{ placement: 'top', duration: 100, maxWidth: 'none' }}
        >
          <Box className={s.tableBubble}>
            <ToolButton label="+ Column" onClick={() => editor.chain().focus().addColumnAfter().run()} />
            <ToolButton label="− Column" onClick={() => editor.chain().focus().deleteColumn().run()} />
            <Divider orientation="vertical" flexItem />
            <ToolButton label="+ Row" onClick={() => editor.chain().focus().addRowAfter().run()} />
            <ToolButton label="− Row" onClick={() => editor.chain().focus().deleteRow().run()} />
            <Divider orientation="vertical" flexItem />
            <ToolButton label="Header row" onClick={() => editor.chain().focus().toggleHeaderRow().run()} />
            <ToolButton label="Delete table" onClick={() => editor.chain().focus().deleteTable().run()} />
          </Box>
        </BubbleMenu>
        <BubbleMenu
          editor={editor}
          pluginKey="kbImageBubble"
          shouldShow={({ editor: current }) => current.isActive('image')}
          tippyOptions={{ placement: 'top', duration: 100, maxWidth: 'none' }}
        >
          <Box className={s.tableBubble}>
            <ToolButton label="25%" onClick={() => setImageWidth('25%')} />
            <ToolButton label="50%" onClick={() => setImageWidth('50%')} />
            <ToolButton label="75%" onClick={() => setImageWidth('75%')} />
            <ToolButton label="Full" onClick={() => setImageWidth('100%')} />
            <Divider orientation="vertical" flexItem />
            <ToolButton label="Left" onClick={() => setImageAlign('left')} />
            <ToolButton label="Center" onClick={() => setImageAlign('center')} />
            <ToolButton label="Right" onClick={() => setImageAlign('right')} />
          </Box>
        </BubbleMenu>
        <Box className={s.content}>
          <EditorContent editor={editor} />
        </Box>
      </Box>
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { overflow: 'visible', marginTop: 6 } }}
      >
        <Box className={s.linkPopover}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyLink()}
            InputLabelProps={{ shrink: true }}
          />
          <Button color="primary" variant="contained" onClick={applyLink}>
            Apply
          </Button>
        </Box>
      </Popover>
      {error && helperText && <Typography className={s.formHelper}>{helperText}</Typography>}
    </>
  )
}

export default TipTapEditor
